import React, { useState, useEffect, useCallback } from 'react';
import '../Common/Layout.css';
import {
  Grid,
  Header,
  Table,
  Button,
  Dimmer,
  Loader,
  Divider,
  Modal,
  Message,
} from 'semantic-ui-react';
import { useStitchAuth } from '../StitchAuth';
import { fetchBodytraceDevices, deleteDevice } from '../../services/RegistryApi';
import { fetchTenoviDevices } from '../../services/TenoviApi';
import { refreshAssignmentTrackingItem } from '../../services/DevicesApi';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import ObjectID from 'bson-objectid';

const BODYTRACE_DEVICE_MAP = {
  BT004: 'Weight Scale',
  BT105: 'BP Cuff',
};

const TENOVI_DEVICE_MAP = {
  TENOVI_BPM: 'Tenovi BPM',
  TENOVI_BPM_XL: 'Tenovi BPM XL',
  TENOVI_GLUCOMETER: 'Tenovi Glucometer',
  TENOVI_PULSE_OX: 'Tenovi Pulse Ox',
  TENOVI_SCALE: 'Tenovi Scale',
};

const TERMINAL_DELIVERED = /^(delivered)$/i;

const twoMonthsAgoIso = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 2);
  return d.toISOString();
};

const toIdString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value.toHexString) return value.toHexString();
  if (value.$oid) return value.$oid;
  return String(value);
};

const buildTrackingUrl = (carrierName, trackNum) => {
  if (!trackNum) return '';
  if (carrierName === 'UPS') {
    return (
      'https://www.ups.com/track?loc=null&tracknum=' +
      trackNum +
      '&requester=WT/trackdetails'
    );
  }
  if (carrierName === 'FEDEX') {
    return (
      'https://www.fedex.com/apps/fedextrack/?tracknumbers=' + trackNum
    );
  }
  return '';
};

const normalizeBodyTraceRow = (device) => ({
  key: `bt-${toIdString(device._id)}`,
  vendor: 'BodyTrace',
  trackingItemId: `registry-${toIdString(device._id)}`,
  memberId: toIdString(device.memberId),
  name: device.name || '',
  phone: device.phone || '',
  deviceLabel: BODYTRACE_DEVICE_MAP[device.device] || device.device || 'Unknown',
  identifier: device.imei || '',
  reference: device.reference || device.order_number || '',
  status: device.status || '',
  statusUpdateTime: device.statusUpdateTime || device.orderDateTime || '',
  orderDate: device.orderDateTime || device.startDate || '',
  tracking: device.tracking || [],
  carrierName: device.carrierName || '',
  trackingLink: '',
  raw: device,
});

const normalizeTenoviRow = (device) => {
  const status =
    device.status ||
    device.tenoviInfo?.status ||
    device.tenoviInfo?.shipping_status ||
    '';
  const trackingLink =
    device.trackingLink ||
    device.tenoviInfo?.shipping_tracking_link ||
    device.tenoviInfo?.device?.fulfillment_request?.shipping_tracking_link ||
    '';
  const name =
    device.tenoviInfo?.device?.fulfillment_request?.shipping_name ||
    device.tenoviInfo?.shipping_name ||
    device.tenoviInfo?.patient?.name ||
    '';
  return {
    key: `tn-${toIdString(device._id)}`,
    vendor: 'Tenovi',
    trackingItemId: `tenovi-${toIdString(device._id)}`,
    memberId: toIdString(device.memberId),
    name,
    phone: device.tenoviInfo?.patient_phone_number || '',
    deviceLabel:
      TENOVI_DEVICE_MAP[device.deviceEnum] || device.deviceEnum || 'Tenovi',
    identifier:
      device.tenoviInfo?.device?.hardware_uuid ||
      device.tenoviInfo?.hardware_uuid ||
      device.tenoviInfo?.id ||
      '',
    reference: device.tenoviInfo?.id || '',
    status,
    statusUpdateTime:
      device.tenoviInfo?.delivered_on ||
      device.tenoviInfo?.shipped_on ||
      device.orderDateTime ||
      device.createdAt ||
      '',
    orderDate:
      device.orderDateTime ||
      device.createdAt ||
      device.tenoviInfo?.created ||
      '',
    tracking: [],
    carrierName: '',
    trackingLink,
    raw: device,
  };
};

const isNotDelivered = (status) =>
  !status || !TERMINAL_DELIVERED.test(String(status).trim());

const isWithinLastTwoMonths = (iso) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return t >= new Date(twoMonthsAgoIso()).getTime();
};

function isValidDate(date) {
  return date instanceof Date && !isNaN(date);
}

function MemberDevicesTable({ data, onOpenDetails }) {
  return (
    <Table basic="very" selectable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Vendor</Table.HeaderCell>
          <Table.HeaderCell>Recipient</Table.HeaderCell>
          <Table.HeaderCell>Phone</Table.HeaderCell>
          <Table.HeaderCell>Device</Table.HeaderCell>
          <Table.HeaderCell>Identifier</Table.HeaderCell>
          <Table.HeaderCell>Reference #</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Tracking</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {_.map(
          _.orderBy(
            data,
            [
              (currEntry) => {
                const currDate = new Date(currEntry.statusUpdateTime);
                return isValidDate(currDate) ? currDate : 0;
              },
            ],
            ['desc'],
          ),
          (row) => (
            <Table.Row
              key={row.key}
              style={{ cursor: 'pointer' }}
              onClick={() => onOpenDetails(row)}
            >
              <Table.Cell>{row.vendor}</Table.Cell>
              <Table.Cell>
                {row.memberId ? (
                  <Link
                    to={`/member-details/${ObjectID(
                      row.memberId,
                    ).toHexString()}/profile`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.name || row.memberId}
                  </Link>
                ) : (
                  row.name || 'N/A'
                )}
              </Table.Cell>
              <Table.Cell>{row.phone || 'N/A'}</Table.Cell>
              <Table.Cell>
                {row.memberId ? (
                  <Link
                    to={`/member-details/${ObjectID(
                      row.memberId,
                    ).toHexString()}/measurements`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.deviceLabel}
                  </Link>
                ) : (
                  row.deviceLabel
                )}
              </Table.Cell>
              <Table.Cell>{row.identifier || 'N/A'}</Table.Cell>
              <Table.Cell>{row.reference || 'N/A'}</Table.Cell>
              <Table.Cell>
                {row.status || 'N/A'}
                <br />
                {row.statusUpdateTime ? (
                  <small>
                    {new Date(row.statusUpdateTime).toLocaleString()}
                  </small>
                ) : null}
              </Table.Cell>
              <Table.Cell>
                {row.trackingLink ? (
                  <a
                    href={row.trackingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Tracking link
                  </a>
                ) : (
                  _.map(row.tracking || [], (currTrack, i) => {
                    const url = buildTrackingUrl(row.carrierName, currTrack);
                    if (!currTrack) {
                      return <span key={i}></span>;
                    }
                    if (!url) {
                      return (
                        <span key={currTrack}>
                          {(row.carrierName
                            ? `${row.carrierName}: `
                            : '') + currTrack}
                        </span>
                      );
                    }
                    return (
                      <a
                        key={currTrack}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.carrierName + ': ' + currTrack}
                      </a>
                    );
                  })
                )}
              </Table.Cell>
            </Table.Row>
          ),
        )}
      </Table.Body>
    </Table>
  );
}

export default function TrackDeliveries() {
  const { userCustomData } = useStitchAuth();
  const [memberDevicesP, setMemberDevicesP] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [refreshingItem, setRefreshingItem] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [refreshError, setRefreshError] = useState('');

  const loadData = useCallback(
    async (mounted) => {
      try {
        const bodyTraceQuery = {
          device: { $in: ['BT004', 'BT105'] },
          $or: [
            { status: { $exists: false } },
            {
              status: {
                $not: { $regex: '^delivered$', $options: 'i' },
              },
            },
          ],
          name: { $ne: '' },
        };
        if (!userCustomData.superUserAccess) {
          bodyTraceQuery.owner_id = userCustomData.group_id;
        }

        const tenoviQuery = {
          deleted: null,
        };
        if (!userCustomData.superUserAccess) {
          tenoviQuery.owner_id = userCustomData.group_id;
        }

        const [registryResponse, tenoviResponse] = await Promise.all([
          fetchBodytraceDevices(bodyTraceQuery),
          fetchTenoviDevices(tenoviQuery).catch((err) => {
            console.error('Error loading Tenovi devices:', err);
            return [];
          }),
        ]);

        const bodyTraceRows = (registryResponse || [])
          .filter((device) => !device.isBulkOrder && device.name !== '')
          .map(normalizeBodyTraceRow);

        const tenoviRows = (tenoviResponse || [])
          .filter((device) => {
            if (device.supplyEnum || device.isBulkOrder) return false;
            if (device.deleted || device.unlinked) return false;
            const status =
              device.status ||
              device.tenoviInfo?.status ||
              device.tenoviInfo?.shipping_status ||
              '';
            if (!isNotDelivered(status)) return false;
            const orderDate =
              device.orderDateTime ||
              device.createdAt ||
              device.tenoviInfo?.created ||
              '';
            return isWithinLastTwoMonths(orderDate);
          })
          .map(normalizeTenoviRow);

        if (mounted) {
          setMemberDevicesP([...bodyTraceRows, ...tenoviRows]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        if (mounted) {
          setMemberDevicesP([]);
        }
      }
    },
    [userCustomData],
  );

  useEffect(() => {
    let mounted = true;
    if (userCustomData) {
      setLoading(true);
      loadData(mounted).finally(() => {
        if (mounted) setLoading(false);
      });
    }
    return () => {
      mounted = false;
    };
  }, [userCustomData, loadData]);

  const handleOpenDetails = (item) => {
    setRefreshMessage('');
    setRefreshError('');
    setSelectedItem(item);
  };

  const handleRefreshFromVendor = async () => {
    if (!selectedItem?.trackingItemId || !selectedItem?.vendor) {
      return;
    }
    setRefreshingItem(true);
    setRefreshMessage('');
    setRefreshError('');
    try {
      const response = await refreshAssignmentTrackingItem({
        id: selectedItem.trackingItemId,
        vendor: selectedItem.vendor,
      });
      const nextItem = response?.item || response?.data?.item;
      const message =
        response?.message ||
        response?.data?.message ||
        `Refreshed from ${selectedItem.vendor}`;

      // Reload list so status/tracking reflect Mongo updates
      await loadData(true);

      if (nextItem) {
        const refreshedRow =
          selectedItem.vendor === 'Tenovi'
            ? normalizeTenoviRow({
                ...selectedItem.raw,
                _id: selectedItem.raw?._id,
                status: nextItem.status,
                trackingLink: nextItem.trackingLink,
                tenoviInfo: {
                  ...(selectedItem.raw?.tenoviInfo || {}),
                  status: nextItem.status,
                },
                memberId: nextItem.memberId || selectedItem.memberId,
                deviceEnum: nextItem.deviceType || selectedItem.raw?.deviceEnum,
              })
            : normalizeBodyTraceRow({
                ...selectedItem.raw,
                status: nextItem.status,
                tracking: nextItem.tracking || selectedItem.tracking,
                carrierName:
                  nextItem.carrierName || selectedItem.carrierName,
                statusUpdateTime: new Date().toISOString(),
              });
        setSelectedItem({
          ...refreshedRow,
          trackingItemId: selectedItem.trackingItemId,
          key: selectedItem.key,
        });
      }

      setRefreshMessage(message);
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        `Failed to refresh from ${selectedItem.vendor}`;
      setRefreshError(apiMessage);
    } finally {
      setRefreshingItem(false);
    }
  };

  const handleCancelRegistry = async (_id) => {
    setLoading(true);
    try {
      await deleteDevice(_id);
      await loadData(true);
    } catch (error) {
      console.error('Error cancelling registry:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="messages box">
      {loading && (
        <div className="messages box">
          <Dimmer active inverted>
            <Loader inverted>Loading delivery tracking...</Loader>
          </Dimmer>
        </div>
      )}
      <Grid style={{ margin: 0 }}>
        <Grid.Row>
          <Grid.Column>
            <small className="grey text">
              NOTES: <br />
              1. Already delivered products are not shown in this
              page.
              <br />
              2. Tenovi devices ordered in the last 2 months that are
              not delivered are included. Click a row to open details
              and refresh from the vendor.
              <br />
              3. To see the devices that are already delivered, please
              use the{' '}
              <Link to="/reports/device/latest">
                Device Management
              </Link>{' '}
              page.
              <br />
              4. If you have any additional questions regarding the
              package delivery, please refer to the Reference # on the
              table when sending an inquiry.
            </small>
          </Grid.Column>
        </Grid.Row>
        <Divider />
        <Grid.Row columns={1}>
          <Grid.Column>
            <Header as="h3">
              Member-assigned Device Requests
              <Header.Subheader>
                # currently active orders: {memberDevicesP.length}
              </Header.Subheader>
            </Header>
            <MemberDevicesTable
              data={memberDevicesP}
              onOpenDetails={handleOpenDetails}
              handleCancelRegistry={handleCancelRegistry}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Modal
        open={!!selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setRefreshMessage('');
          setRefreshError('');
        }}
        size="small"
        closeIcon
      >
        <Modal.Header>
          Delivery detail — {selectedItem?.deviceLabel || ''}
        </Modal.Header>
        <Modal.Content>
          {selectedItem && (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  marginBottom: '0.75rem',
                }}
              >
                <div>
                  <p style={{ marginBottom: '0.35rem' }}>
                    <strong>Vendor:</strong> {selectedItem.vendor}
                    {' · '}
                    <strong>Status:</strong>{' '}
                    {selectedItem.status || 'N/A'}
                  </p>
                  <p style={{ marginBottom: '0.35rem' }}>
                    <strong>Recipient:</strong>{' '}
                    {selectedItem.name || 'N/A'}
                    {selectedItem.phone
                      ? ` · ${selectedItem.phone}`
                      : ''}
                  </p>
                  <p style={{ marginBottom: '0.35rem' }}>
                    <strong>Identifier:</strong>{' '}
                    {selectedItem.identifier || 'N/A'}
                    {selectedItem.reference
                      ? ` · Reference: ${selectedItem.reference}`
                      : ''}
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    <strong>Ordered:</strong>{' '}
                    {selectedItem.orderDate
                      ? new Date(
                          selectedItem.orderDate,
                        ).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
                <Button
                  primary
                  size="small"
                  loading={refreshingItem}
                  disabled={refreshingItem}
                  onClick={handleRefreshFromVendor}
                  content={
                    selectedItem.vendor === 'Tenovi'
                      ? 'Refresh from Tenovi'
                      : 'Refresh from BodyTrace'
                  }
                  icon="refresh"
                  labelPosition="left"
                />
              </div>

              {refreshMessage && (
                <Message
                  positive
                  size="small"
                  onDismiss={() => setRefreshMessage('')}
                  content={refreshMessage}
                />
              )}
              {refreshError && (
                <Message
                  negative
                  size="small"
                  onDismiss={() => setRefreshError('')}
                  content={refreshError}
                />
              )}
            </>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
}
